import React, { useRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Grid, Card, CardHeader, CardContent, Typography, Button, Box,
    IconButton, Chip, Tooltip, CircularProgress
} from '@mui/material';
import {
    Save as IconSave,
    Close as IconClose,
    Person as IconPerson,
    Badge as IconBadge,
    Phone as IconPhone,
    Home as IconHome,
    HistoryEdu as IconHistoryEdu
} from "@mui/icons-material";
import { PaperComponentDraggable } from "./PaperComponentDraggable";
import PersonaNatural from './PersonaNatural';
import PersonDirection from './PersonDirection';
import PersonDocument from './PersonDocument';
import PersonContact from './PersonContact';
import PersonPast from './PersonPast';
import { activeValidationsPersonaNatural, setTipoPersona, initPerson } from "../redux/personSlice";
import { activeValidationsPersonaDocumento } from "../redux/personDocumentSlice";
import { setPersonaDirecciones } from '../redux/personDirectionSlice';
import { setPersonaContactos } from '../redux/personContactSlice';
import { setPersonaDocumentos } from '../redux/personDocumentSlice';
import { setPersonaAntecedentes } from '../redux/personPastSlice';
import * as personaService from "../services/personaService";
import { notifySuccess, notifyWarning, notifyError } from "../services/notificationService";

const PersonRegister = (props) => {
    const dispatch = useDispatch();
    const dialogRef = useRef();
    const sliceKey = props.sliceKey;
    const initialized = useRef(false);

    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        if (props.open && !initialized.current) {
            dispatch(initPerson({ sliceKey: sliceKey }));
            if (props.tipoPersona) {
                dispatch(setTipoPersona({ sliceKey, tipoPersona: props.tipoPersona }));
            }
            initialized.current = true;
        }
        if (!props.open) {
            initialized.current = false;
        }
    }, [props.open, dispatch, sliceKey, props.tipoPersona]);

    const personaNatural = useSelector((state) => state.person[sliceKey]?.personaNatural);
    const personaDirecciones = useSelector((state) => state.personDirection[sliceKey]?.personaDirecciones);
    const personaDocumentos = useSelector((state) => state.personDocument[sliceKey]?.personaDocumentos);
    const personaContactos = useSelector((state) => state.personContact[sliceKey]?.personaContactos);
    const personaVinculaciones = useSelector((state) => state.personBonding[sliceKey]?.personaVinculaciones);
    const personaAntecedentes = useSelector((state) => state.personPast[sliceKey]?.personaAntecedentes);
    const personaNaturalValidation = useSelector((state) => state.person[sliceKey]?.validationMessage);
    const personaDocumentosValidation = useSelector((state) => state.personDocument[sliceKey]?.validationMessage);

    const defaultPersonaDirecciones = useSelector((state) => state.personDirection[sliceKey]?.defaultPersonaDirecciones);
    const defaultPersonaDocumentos = useSelector((state) => state.personDocument[sliceKey]?.defaultPersonaDocumentos);
    const defaultPersonaContactos = useSelector((state) => state.personContact[sliceKey]?.defaultPersonaContactos);
    const defaultPersonaAntecedentes = useSelector((state) => state.personPast[sliceKey]?.defaultPersonaAntecedentes);

    const esEdicion = Boolean(props.id && props.id > 0);
    const etiquetaTipo = props.tipoPersona || "Persona";

    const nombreCompletoActual = [
        personaNatural?.primerApellido,
        personaNatural?.segundoApellido,
        personaNatural?.primerNombre,
        personaNatural?.segundoNombre
    ].map(s => (s || "").trim()).filter(s => s.length > 0 && s.toLowerCase() !== "null").join(" ");

    const guardarPersona = async (cerrarAlFinal = true) => {
        const nombreCompleto = [
            personaNatural?.primerApellido,
            personaNatural?.segundoApellido,
            personaNatural?.primerNombre,
            personaNatural?.segundoNombre
        ].map(s => (s || "").trim()).filter(s => s.length > 0 && s.toLowerCase() !== "null").join(" ");

        const dataPost = {
            ...personaNatural,
            nombres: nombreCompleto,
            idSexo: parseInt(personaNatural?.idSexo || 0, 10),
            personaDireccion: personaDirecciones || [],
            personaDocumento: personaDocumentos || [],
            personaContacto: personaContactos || [],
            personaVinculacion: personaVinculaciones || [],
            personaAntecedente: personaAntecedentes || []
        };

        const validationMessages = (personaNaturalValidation || []).concat(personaDocumentosValidation || []);

        if (validationMessages.length > 0) {
            dispatch(activeValidationsPersonaNatural({ sliceKey }));
            dispatch(activeValidationsPersonaDocumento({ sliceKey }));
            notifyWarning(validationMessages.join(" - "));
            return false;
        }

        setGuardando(true);
        try {
            const response = await personaService.postPersonaNaturalUpdateWithDetails(dataPost);
            const rawId = response?.data?.data ?? response?.data ?? 0;
            const nuevoId = parseInt(rawId, 10) || personaNatural?.id || props.id;

            notifySuccess(esEdicion ? "Datos de la persona actualizados con éxito" : "Persona registrada con éxito");

            if (personaNatural?.id > 0) {
                const directions = await personaService.getsPersonaDireccionView(personaNatural.id);
                const contacts = await personaService.getsPersonaContactoView(personaNatural.id);
                const documents = await personaService.getsPersonaDocumentoView(personaNatural.id);
                const pasts = await personaService.getsPersonaAntecedenteView(personaNatural.id);

                dispatch(setPersonaDirecciones({ sliceKey, data: directions?.length ? directions : defaultPersonaDirecciones }));
                dispatch(setPersonaContactos({ sliceKey, data: contacts?.length ? contacts : defaultPersonaContactos }));
                dispatch(setPersonaDocumentos({ sliceKey, data: documents?.length ? documents : defaultPersonaDocumentos }));
                dispatch(setPersonaAntecedentes({ sliceKey, data: pasts?.length ? pasts : defaultPersonaAntecedentes }));
            }

            if (cerrarAlFinal) {
                if (props.setNewPersonSaved && nuevoId > 0) {
                    props.setNewPersonSaved(nuevoId, nombreCompleto);
                }
                props.openClose();
            }
            return true;
        } catch (error) {
            notifyError(error.response?.data?.message ?? error.message ?? "Error inesperado al guardar la persona.");
            return false;
        } finally {
            setGuardando(false);
        }
    };

    return (
        <Dialog
            open={Boolean(props.open)}
            onClose={guardando ? undefined : props.openClose}
            fullWidth={true}
            maxWidth="lg"
            ref={dialogRef}
            PaperComponent={PaperComponentDraggable}
            aria-labelledby="draggable-dialog-title"
            sx={{
                "& .MuiDialog-paper": {
                    borderRadius: 2.5,
                    overflow: "hidden"
                }
            }}
        >
            <DialogTitle
                id="draggable-dialog-title"
                sx={{
                    cursor: "move",
                    p: 2,
                    px: 2.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background: "linear-gradient(135deg, #1e293b 0%, #334155 100%)",
                    color: "#fff",
                    userSelect: "none"
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <Box sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        backgroundColor: "rgba(255, 255, 255, 0.15)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0
                    }}>
                        <IconPerson sx={{ color: "#fff", fontSize: 24 }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: "#fff", lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {esEdicion ? `Editar ${etiquetaTipo}` : `Registrar ${etiquetaTipo}`}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.75)", display: "block" }}>
                            {esEdicion
                                ? (nombreCompletoActual ? `${nombreCompletoActual} (ID: ${props.id})` : `ID Persona: ${props.id}`)
                                : `Complete los datos personales para vincular ${etiquetaTipo.toLowerCase()}`}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                    <Chip
                        label={esEdicion ? "Edición" : "Nuevo"}
                        size="small"
                        sx={{
                            backgroundColor: esEdicion ? "#0284c7" : "#059669",
                            color: "#fff",
                            fontWeight: 600,
                            fontSize: "0.75rem"
                        }}
                    />
                    <Tooltip title="Cerrar ventana">
                        <span>
                            <IconButton
                                aria-label="Cerrar"
                                onClick={props.openClose}
                                disabled={guardando}
                                size="small"
                                sx={{
                                    color: "rgba(255, 255, 255, 0.8)",
                                    "&:hover": { color: "#fff", backgroundColor: "rgba(255, 255, 255, 0.15)" }
                                }}
                            >
                                <IconClose />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </DialogTitle>

            <DialogContent
                dividers
                sx={{
                    p: { xs: 1.5, sm: 2.5 },
                    maxHeight: "calc(85vh - 130px)",
                    minHeight: "380px",
                    backgroundColor: "#f8fafc"
                }}
            >
                <Grid container spacing={2}>
                    {/* Sección 1: Datos Personales */}
                    <Grid item xs={12}>
                        <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
                            <CardHeader
                                avatar={<IconPerson color="primary" fontSize="small" />}
                                title={<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Datos Personales</Typography>}
                                subheader={<Typography variant="caption" color="text.secondary">Nombres, apellidos, nacimiento y datos generales</Typography>}
                                sx={{ pb: 0, pt: 1.5, px: 2 }}
                            />
                            <CardContent sx={{ pt: 1, px: 2, pb: "16px !important" }}>
                                <PersonaNatural sliceKey={sliceKey} hideInputs={props.hideInputs} id={props.id} autoFocus />
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Sección 2: Documentos de Identidad */}
                    <Grid item xs={12} md={6}>
                        <Card variant="outlined" sx={{ borderRadius: 2, height: "100%", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
                            <CardHeader
                                avatar={<IconBadge color="primary" fontSize="small" />}
                                title={<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Documentos de Identidad</Typography>}
                                subheader={<Typography variant="caption" color="text.secondary">DNI, carnet de extranjería u otros documentos</Typography>}
                                sx={{ pb: 0, pt: 1.5, px: 2 }}
                            />
                            <CardContent sx={{ pt: 1, px: 2, pb: "16px !important" }}>
                                <PersonDocument sliceKey={sliceKey} id={props.id} />
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Sección 3: Contactos */}
                    <Grid item xs={12} md={6}>
                        <Card variant="outlined" sx={{ borderRadius: 2, height: "100%", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
                            <CardHeader
                                avatar={<IconPhone color="primary" fontSize="small" />}
                                title={<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Datos de Contacto</Typography>}
                                subheader={<Typography variant="caption" color="text.secondary">Teléfono, celular, correo electrónico</Typography>}
                                sx={{ pb: 0, pt: 1.5, px: 2 }}
                            />
                            <CardContent sx={{ pt: 1, px: 2, pb: "16px !important" }}>
                                <PersonContact sliceKey={sliceKey} id={props.id} />
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Sección 4: Dirección */}
                    <Grid item xs={12}>
                        <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
                            <CardHeader
                                avatar={<IconHome color="primary" fontSize="small" />}
                                title={<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Dirección / Domicilio</Typography>}
                                subheader={<Typography variant="caption" color="text.secondary">Ubigeo y dirección residencial</Typography>}
                                sx={{ pb: 0, pt: 1.5, px: 2 }}
                            />
                            <CardContent sx={{ pt: 1, px: 2, pb: "16px !important" }}>
                                <PersonDirection sliceKey={sliceKey} id={props.id} />
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Sección 5: Antecedentes */}
                    <Grid item xs={12}>
                        <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
                            <CardHeader
                                avatar={<IconHistoryEdu color="primary" fontSize="small" />}
                                title={<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Antecedentes</Typography>}
                                subheader={<Typography variant="caption" color="text.secondary">Historial médico o antecedentes relevantes</Typography>}
                                sx={{ pb: 0, pt: 1.5, px: 2 }}
                            />
                            <CardContent sx={{ pt: 1, px: 2, pb: "16px !important" }}>
                                <PersonPast sliceKey={sliceKey} id={props.id} />
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions
                sx={{
                    p: 2,
                    px: 3,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    backgroundColor: "#fff",
                    borderTop: "1px solid #e2e8f0"
                }}
            >
                <Typography variant="caption" color="text.secondary">
                    * Campos obligatorios para el registro
                </Typography>
                <Box sx={{ display: "flex", gap: 1.5 }}>
                    <Button
                        variant="outlined"
                        color="inherit"
                        onClick={props.openClose}
                        disabled={guardando}
                        sx={{ textTransform: "none", fontWeight: 500 }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => guardarPersona(true)}
                        disabled={guardando}
                        startIcon={guardando ? <CircularProgress size={18} color="inherit" /> : <IconSave />}
                        sx={{ textTransform: "none", fontWeight: 600, minWidth: 160 }}
                    >
                        {guardando ? "Guardando..." : (esEdicion ? "Guardar Cambios" : "Guardar y Aceptar")}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default PersonRegister;
